import {
    getAttributeColumns,
    getAxisConfig,
    getChartType,
    getMeasureColumns,
    getYAxisColumns,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import { isGeoChartType } from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { getConditionalFormats } from '@thoughtspot/chart-lib/lib/core/conditional-formatting-utils';
import { getTotalRowCount } from '@thoughtspot/chart-lib/lib/data/data-util';
import {
    getFormattingFromColumn,
    getUnit,
} from '@thoughtspot/formatter-service';
import {
    isEffectivelyNumeric,
    isMeasure,
    isNumeric,
} from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import {
    AccessMode,
    Answer,
    AnswerColumn,
    AxisConfig,
    AxisObj,
    ChartType,
    ChartViz,
    ColumnSortCategory,
    CustomChartManifest,
    DisplayMode,
    FalconDataType,
    FilterGroup,
    GeoConfigType,
    HeadlineViz,
    ListType,
    ObjectPermission,
    Source,
    TableViz,
    Unit,
    Visualization,
    VizColumn,
} from '/@services/generated/graphql-types';
import { textContainsDynamicTitle } from './dynamic-title.util';

export interface MixpanelAnswerCommonProperties {
    objectId: string;
    objectType: string;
    objectAuthorId: string;
    objectCreatedAt: string;
    vizType: DisplayMode;
    vizChartType: ChartType;
    geoChartType: string;
    totalColumns: number;
    VisualizedColumns: number;
    nonVisualizedColumns: number;
    numberColumns: number;
    stringColumns: number;
    dateColumns: number;
    booleanColumns: number;
    totalFilters: number;
    stringFilters: number;
    dateFilters: number;
    booleanFilters: number;
    numberFilters: number;
    numberFormattingApplied: boolean;
    conditionalFormattingApplied: boolean;
    answerTitleLength: number;
    answerDescriptionLength: number;
    dataSourceGuids: Array<string>;
    dataSourceTypes: Array<string>;
    dataSourceAuthors: Array<string>;
    dataSourceSchema: Array<string>;
    pivotValues: string;
    numberOfRenamedColumns: number;
}

export const GEO_CHART_MAP_TYPE = {
    NA: 'NA',
    CUSTOM: 'Custom',
    TS_UPLOADED: 'TSUploaded',
    STANDARD_REGION: 'StandardRegion',
};

const hasConditionalFormat = (
    vizClientState: any,
    columnId: string,
    typename: 'ChartVizProps' | 'TableVizProps' | 'HeadlineVizProps',
    vizModel: ChartViz | TableViz,
) => {
    return !!_.size(
        getConditionalFormats(vizClientState, columnId, typename, vizModel),
    );
};

const getNoOfColumnsWithConditionalFormat = (
    columns: AnswerColumn[],
    clientState: any,
    typename: 'HeadlineVizProps' | 'ChartVizProps' | 'TableVizProps',
    vizModel: ChartViz | TableViz,
) => {
    return _.size(
        _.filter(columns, (answerColumn: AnswerColumn) => {
            return hasConditionalFormat(
                clientState,
                answerColumn.id,
                typename,
                vizModel,
            );
        }),
    );
};

const getNoOfColumnsWithNumberFormat = (columns: AnswerColumn[]) => {
    return _.size(
        _.filter(columns, (answerColumn: AnswerColumn) => {
            return getUnit(getFormattingFromColumn(answerColumn)) !== Unit.Auto;
        }),
    );
};

const getNoOfRenamedColumns = (columns: AnswerColumn[]) => {
    return _.size(
        _.filter(columns, (answerColumn: AnswerColumn) => {
            return answerColumn.isUserDefinedTitle;
        }),
    );
};

const getNoOfCustomSortedColumns = (viz: TableViz | ChartViz): number => {
    let noOfCustomSortColumns = 0;

    if (viz) {
        // Create list of columns which has model level custom order.
        const columnIdsWithCustomOrder = viz.columns.filter(
            d => d.column.customOrder && d.column.customOrder.length > 0,
        );
        viz?.sortInfo.forEach(info => {
            // Single pass through sortConfig to count both categories
            const isCustomSorted =
                info.category === ColumnSortCategory.Custom &&
                info.customOrder.length > 0;

            const isDefaultSorted =
                (info.category === ColumnSortCategory.Default ||
                    !info.category) && // Backward compatibility for old sort config
                columnIdsWithCustomOrder.findIndex(
                    col => col.column.id === info.columnId,
                ) > -1;

            if (isCustomSorted || isDefaultSorted) {
                noOfCustomSortColumns++;
            }
        });
    }
    return noOfCustomSortColumns;
};

export const getVizEventProperties = (
    vizId: string,
    answer?: Partial<Answer>,
    vizModel?: Visualization,
) => {
    // This method is called at various places
    // All we need here is vizModel
    // If vizModel is available just pass that
    // otherwise vizId and answer
    const viz: TableViz | HeadlineViz =
        vizModel ??
        _.find(answer?.visualizations, {
            id: vizId,
        });
    const clientState = viz?.vizProp;

    // eslint-disable-next-line no-underscore-dangle
    if (viz && (viz as TableViz).__typename === 'TableViz') {
        const tableViz = viz as TableViz;
        const measureColumns = getMeasureColumns(tableViz);
        const attributeColumns = getAttributeColumns(tableViz);
        return {
            vizType: 'TABLE',
            numberOfMeasures: _.size(measureColumns),
            numberOfMeasuresWithNumberFormat: getNoOfColumnsWithNumberFormat(
                measureColumns,
            ),
            numberOfMeasuresWithConditionalFormat: getNoOfColumnsWithConditionalFormat(
                measureColumns,
                clientState,
                'TableVizProps',
                tableViz,
            ),
            numberOfStringAttr: _.size(attributeColumns),
            numberOfStringAttrWithConditionalFormat: getNoOfColumnsWithConditionalFormat(
                attributeColumns,
                clientState,
                'TableVizProps',
                tableViz,
            ),
        };
    }

    // eslint-disable-next-line no-underscore-dangle
    if (viz && (viz as HeadlineViz).__typename === 'HeadlineViz') {
        const headlineColumn = (viz as HeadlineViz).headlineColumn;
        const isMeasure = isNumeric(headlineColumn);

        const numberOfColumnsWithConditionalFormat = hasConditionalFormat(
            clientState,
            headlineColumn.id,
            'HeadlineVizProps',
            null,
        )
            ? 1
            : 0;

        return {
            vizType: 'HEADLINE',
            numberOfMeasures: isMeasure ? 1 : 0,
            numberOfMeasuresWithNumberFormat:
                isMeasure &&
                getUnit(getFormattingFromColumn(headlineColumn)) !== Unit.Auto
                    ? 1
                    : 0,
            numberOfMeasuresWithConditionalFormat:
                isMeasure && numberOfColumnsWithConditionalFormat,
            numberOfStringAttr: isMeasure ? 0 : 1,
            numberOfStringAttrWithConditionalFormat:
                !isMeasure && numberOfColumnsWithConditionalFormat,
        };
    }

    // TODO: implement mixpanel event properties for chartViz
    return {
        vizType: 'CHART',
    };
};

export enum AnswerPageContext {
    UNSAVED_ANSWER = 'UNSAVED_ANSWER',
    SAVED_ANSWER = 'SAVED_ANSWER',
    VIEW_ANSWER = 'VIEW_ANSWER',
    COHORT_ANSWER = 'COHORT_ANSWER',
    PINBOARD_VIZ = 'PINBOARD_VIZ',
    PINBOARD_VIZ_EDIT_MODAL = 'PINBOARD_VIZ_EDIT_MODAL',
    SPOTIQ_VIZ = 'SPOTIQ_VIZ',
    SPOTIQ_VIZ_EDIT_MODAL = 'SPOTIQ_VIZ_EDIT_MODAL',
    EXPLORE = 'EXPLORE',
    PRESENT = 'PRESENT',
    CONV_ASSIST_ANSWER = 'CONV_ASSIST_ANSWER',
    INPUT_TABLE = 'INPUT_TABLE',
}

export const getSourceFromPageContext = (pageContext: AnswerPageContext) => {
    switch (pageContext) {
        case AnswerPageContext.EXPLORE:
            return 'Explore';
        case AnswerPageContext.SAVED_ANSWER:
            return 'Saved Answer';
        case AnswerPageContext.PINBOARD_VIZ:
        case AnswerPageContext.PINBOARD_VIZ_EDIT_MODAL:
            return 'Liveboard Page';
        case AnswerPageContext.VIEW_ANSWER:
        case AnswerPageContext.UNSAVED_ANSWER:
        case AnswerPageContext.COHORT_ANSWER:
        case AnswerPageContext.CONV_ASSIST_ANSWER:
            return 'Answer Page';
        case AnswerPageContext.INPUT_TABLE:
            return 'Answer Page';
        default:
            return '';
    }
};

export const getCommonAnswerPageProperties = (answerSessionData?: any) => {
    if (_.isEmpty(answerSessionData?.answer)) {
        return {};
    }
    const dataSourceGuids = [] as Array<string>;
    const dataSourceTypes = [''];
    const dataSourceAuthors = [] as Array<string>;
    const dataSourceSchema = [] as Array<string>;
    const columnIds = [] as Array<string>;
    const filterGroups = answerSessionData?.answer?.filterGroups || [];

    let numberColumns = 0;
    let stringColumns = 0;
    let dateColumns = 0;
    let booleanColumns = 0;
    let booleanFilters = 0;
    let stringFilters = 0;
    let dateFilters = 0;
    let numberFilters = 0;
    let filtersCount = 0;

    let pivotValues = 'NA';
    let yAxisMeasures = 0;
    let yAxisAttributes = 0;
    let customChartManifest: Partial<CustomChartManifest> = {};

    answerSessionData?.answer.sources.forEach((source: Source) => {
        dataSourceGuids.push(source.header.guid);
        dataSourceAuthors.push(source.author?.guid);
        dataSourceSchema.push(source.schema?.displayName);
    });

    const accessibleSourceGuids: string[] =
        answerSessionData?.answer.sources
            ?.filter((source: Source) => source.isAccessible)
            ?.map((source: Source) => source.header.guid) ?? [];

    const chartViz = answerSessionData?.answer.visualizations.filter(
        (visualization: ChartViz | TableViz | HeadlineViz) => {
            // eslint-disable-next-line no-underscore-dangle
            return visualization.__typename === 'ChartViz';
        },
    );

    const tableViz = answerSessionData?.answer.visualizations.filter(
        (visualization: ChartViz | TableViz | HeadlineViz) => {
            // eslint-disable-next-line no-underscore-dangle
            return visualization.__typename === 'TableViz';
        },
    );
    const columns = tableViz?.[0]?.columns.map((answerColumn: VizColumn) => {
        return answerColumn.column;
    });

    const measureColumns = getMeasureColumns(tableViz);

    // eslint-disable-next-line no-unused-expressions
    chartViz?.[0]?.columns.forEach((answerColumn: VizColumn) => {
        const dataType = answerColumn.column.dataType;
        columnIds.push(answerColumn.column.id);
        switch (dataType) {
            case FalconDataType.Bool: {
                booleanColumns += 1;
                break;
            }
            case FalconDataType.Char: {
                stringColumns += 1;
                break;
            }
            case FalconDataType.Date:
            case FalconDataType.DateTime:
            case FalconDataType.Time: {
                dateColumns += 1;
                break;
            }
            case FalconDataType.Int32:
            case FalconDataType.Int64:
            case FalconDataType.Float:
            case FalconDataType.Double: {
                numberColumns += 1;
                break;
            }
            default: {
                break;
            }
        }
    });

    filterGroups?.forEach(({ filters, columnInfo }: FilterGroup) => {
        filtersCount += filters.length;
        const dataType = columnInfo.dataType;
        switch (dataType) {
            case FalconDataType.Bool: {
                booleanFilters += 1;
                break;
            }
            case FalconDataType.Char: {
                stringFilters += 1;
                break;
            }
            case FalconDataType.Date:
            case FalconDataType.DateTime:
            case FalconDataType.Time: {
                dateFilters += 1;
                break;
            }
            case FalconDataType.Int32:
            case FalconDataType.Int64:
            case FalconDataType.Float:
            case FalconDataType.Double: {
                numberFilters += 1;
                break;
            }
            default: {
                break;
            }
        }
    });

    const getGeoChartType = () => {
        const chartType = getChartType(chartViz?.[0]);
        if (!chartType || !isGeoChartType(chartType)) {
            return GEO_CHART_MAP_TYPE.NA;
        }
        const geoConfigType = chartViz[0]?.config?.axisConfig[0]?.x?.map(
            (xAxis: AnswerColumn) => {
                return xAxis.geoConfig?.type;
            },
        );
        switch (geoConfigType[0]) {
            case GeoConfigType.CustomRegion:
                return GEO_CHART_MAP_TYPE.CUSTOM;
            case GeoConfigType.AdminDiv_0:
            case GeoConfigType.AdminDiv_1:
            case GeoConfigType.AdminDiv_2:
            case GeoConfigType.Latitude:
            case GeoConfigType.Longitude:
            case GeoConfigType.ZipCode:
                return GEO_CHART_MAP_TYPE.TS_UPLOADED;
            default:
                return GEO_CHART_MAP_TYPE.STANDARD_REGION;
        }
    };

    const xAxisGuid =
        chartViz?.[0]?.config?.axisConfig[0]?.x?.map((xAxis: AnswerColumn) => {
            return xAxis.id;
        }) || [];

    const yAxisGuid =
        chartViz?.[0]?.config?.axisConfig[0]?.y?.map((yAxis: AnswerColumn) => {
            if (isEffectivelyNumeric(yAxis)) {
                yAxisMeasures += 1;
            } else {
                yAxisAttributes += 1;
            }

            return yAxis.id;
        }) || [];

    const colorGuid =
        chartViz?.[0]?.config?.axisConfig[0]?.color?.map(
            (color: AnswerColumn) => {
                return color.id;
            },
        ) || [];

    const categoryGuid =
        chartViz?.[0]?.config?.axisConfig[0]?.category?.map(
            (category: AnswerColumn) => {
                return category.id;
            },
        ) || [];
    let kpiChartType;
    if (chartViz?.[0]?.config.chartType === ChartType.Kpi) {
        kpiChartType =
            chartViz[0]?.config?.axisConfig[0]?.x?.length > 0
                ? 'time-series'
                : 'single-value-numeric';
    }
    if (chartViz?.[0]?.config.chartType === ChartType.PivotTable) {
        if (yAxisMeasures > 0 && yAxisAttributes > 0) {
            pivotValues = 'Combination';
        } else if (yAxisMeasures > 0) {
            pivotValues = 'Measures';
        } else {
            pivotValues = 'Attributes';
        }
    }
    if (chartViz?.[0]?.config.chartType === ChartType.CustomChart) {
        const manifestObject = chartViz?.[0]?.config?.customChartManifest;
        if (!_.isNil(manifestObject)) {
            customChartManifest = {
                ...manifestObject,
            };
        }
    }

    const columnsWithDynamicParameterCount = columns?.reduce(
        (count: number, column: AnswerColumn) =>
            textContainsDynamicTitle(column.dynamicTitle) ? count + 1 : count,
        0,
    );

    const hasAxisDynamicParameter = chartViz?.[0]?.vizProp?.axisProperties?.some(
        (item: AxisObj) =>
            textContainsDynamicTitle(item?.properties?.dynamicTitle),
    );

    const hasDescriptionDynamicParameter = textContainsDynamicTitle(
        answerSessionData?.answer?.dynamicDescription,
    );

    const hasTitleDynamicParameter = textContainsDynamicTitle(
        answerSessionData?.answer?.dynamicName,
    );

    return {
        objectId: answerSessionData?.answer.id,
        answerSessionId: answerSessionData?.id.sessionId,
        source: getSourceFromPageContext(answerSessionData?.pageContext),
        pageContext: answerSessionData?.pageContext,
        objectType: ListType.QuestionAnswerBook,
        objectAuthorId: answerSessionData?.answer.metadata.authorId,
        objectCreatedAt: answerSessionData?.answer.metadata.createdAt,
        vizType: answerSessionData?.answer.displayMode,
        vizChartType: chartViz?.[0]?.config.chartType || ChartType.None,
        kpiChartType,
        geoChartType: getGeoChartType(),
        totalColumns: tableViz?.[0]?.columns.length,
        VisualizedColumns:
            xAxisGuid.length +
            yAxisGuid.length +
            colorGuid.length +
            categoryGuid.length,
        nonVisualizedColumns:
            tableViz?.[0]?.columns.length -
            (xAxisGuid.length +
                yAxisGuid.length +
                colorGuid.length +
                categoryGuid.length),
        numberColumns,
        stringColumns,
        dateColumns,
        booleanColumns,
        totalFilters: filtersCount,
        stringFilters,
        dateFilters,
        booleanFilters,
        numberFilters,
        numberFormattingApplied:
            getNoOfColumnsWithNumberFormat(measureColumns) > 0,
        conditionalFormattingApplied:
            getNoOfColumnsWithConditionalFormat(
                columns,
                tableViz?.[0]?.vizProp,
                'TableVizProps',
                tableViz?.[0],
            ) > 0,
        answerTitleLength: answerSessionData?.answer.name.length,
        answerDescriptionLength: answerSessionData?.answer.description.length,
        dataSourceGuids,
        worksheetId: accessibleSourceGuids[0] ?? dataSourceGuids[0],
        dataSourceTypes,
        dataSourceAuthors,
        pivotValues,
        numberOfRenamedColumns: getNoOfRenamedColumns(columns),
        numberOfCustomSortedColumns: getNoOfCustomSortedColumns(tableViz?.[0]),
        // custom chart properties
        customChartName: customChartManifest?.name ?? '',
        customChartGuid: customChartManifest?.id ?? '',
        customChartIconUrl: customChartManifest?.iconUrl ?? '',
        customChartSourceUrl: customChartManifest?.sourceUrl ?? '',
        isWatchList: !!answerSessionData?.isWatchList,
        columnsWithDynamicParameter: columnsWithDynamicParameterCount,
        hasAxisDynamicParameter,
        hasTitleDynamicParameter,
        hasDescriptionDynamicParameter,
    };
};

export const enum AccessType {
    VIEW = 'View',
    VIEW_AND_INTERACT = 'View & interact',
    EDIT = 'Edit',
}

export interface MixpanelPivotTableProperties {
    visibleRows: number;
    rowFields: number;
    columnFields: number;
    totalRowCount: number;
    measureFields: number;
}

export const getPivotTableEventProperties = (
    vizModel: ChartViz,
    visibleRowCount: number,
): MixpanelPivotTableProperties => {
    const axisConfig: AxisConfig = getAxisConfig(vizModel);
    const { x, y, color } = axisConfig;

    return {
        visibleRows: visibleRowCount,
        rowFields: _.size(x),
        columnFields: _.size(color),
        measureFields: _.size(y),
        totalRowCount: getTotalRowCount(vizModel),
    };
};

export function getAccessTypeForObject(permission: ObjectPermission) {
    if (permission?.objectAccessLevel === AccessMode.Modify) {
        return AccessType.EDIT;
    }
    if (permission?.dataSourceAccessLevel === AccessMode.NoAccess) {
        return AccessType.VIEW;
    }
    return AccessType.VIEW_AND_INTERACT;
}
